import { createContext, useCallback, useContext, useEffect, useState } from 'react'
import { ApolloError } from '@apollo/client'
import { ServerError } from '@apollo/client/link/utils'
import { googleLogout } from '@react-oauth/google'
import { client } from 'config/apolloClient'
import { clearAuthToken, setAuthToken } from 'config/authTokenLink'
import { isInvalidMfaTokenError } from 'config/errorLink'
import { clearMfaToken, getMfaToken, setMfaToken } from 'config/mfaTokenLink'
import {
  clearWebSession,
  endWebSession,
  exchangeWebSessionGrant,
  hasWebSession
} from 'config/webSession'
import { LocationPaths } from 'location.types'
import { useNavigate } from 'react-router'
import {
  ISignInUserMutation,
  ThirdPartySignInProvider,
  useCurrentUserQuery,
  useSignInThirdPartyUserMutation,
  useSignInUserMutation,
  useSignOutUserMutation,
  FeatureGrant,
  UserRole,
  useAuthorizedFeaturesQuery
} from 'types'
import { snackBarVisibleVar } from './ui'

export interface ILoginData {
  email: string
  password: string
}

const WAITING_FOR_PROVIDER = 'Waiting for provider'
const INVALID_CREDENTIALS = 'Invalid credentials'
const USE_GOOGLE_LOGIN = 'Please use Google login'
const NOT_AUTHORIZED =
  'Before you use the Health Portal you need to ask your manager for permission'
const LOGIN_FAILED = 'Unknown error. Talk with the engineering team'
const NETWORK_ISSUE = 'Network issue, try again'

export interface IBasicAuthLoginResult {
  response?: ISignInUserMutation
  mfaRequired: boolean
}

const defaultValues = {
  isAuthorized: (_feature: FeatureGrant) => false,
  loading: false,
  basicAuthLogin: async (_: ILoginData): Promise<IBasicAuthLoginResult> => {
    throw WAITING_FOR_PROVIDER
  },
  googleLogin: async (_: string): Promise<void> => {
    throw WAITING_FOR_PROVIDER
  },
  completeMfaChallenge: async (_mfaToken: string): Promise<void> => {
    throw WAITING_FOR_PROVIDER
  },
  logout: async (): Promise<void> => {}
}

export const AuthContext = createContext(defaultValues)
export const useAuth = () => useContext(AuthContext)

export const AuthProvider = ({ children }: { children: JSX.Element }) => {
  const { data: { currentUser } = {}, loading } = useCurrentUserQuery()
  const [loggedIn, setLoggedIn] = useState(false)
  const navigate = useNavigate()

  const [loginFn] = useSignInUserMutation({
    onError: (e) => {
      if (e.networkError && (e.networkError as ServerError).statusCode === 401) {
        throw INVALID_CREDENTIALS
      }
      throw NETWORK_ISSUE
    }
  })

  const [googleLoginFn] = useSignInThirdPartyUserMutation({
    onError: (e) => {
      if (e.networkError && (e.networkError as ServerError).statusCode === 401) {
        throw INVALID_CREDENTIALS
      }
      throw NETWORK_ISSUE
    }
  })

  const {
    data: authorizedFeaturesData,
    loading: authorizing,
    refetch: getAuthorizedFeatures
  } = useAuthorizedFeaturesQuery()

  useEffect(() => {
    setLoggedIn(!!currentUser)
  }, [currentUser])

  const clearSignedInState = () => {
    client.clearStore()
    localStorage.clear()
    setLoggedIn(false)
    googleLogout()
    navigate(LocationPaths.Login)
  }

  const [logoutFn] = useSignOutUserMutation({
    onError: () => {
      snackBarVisibleVar({
        open: true,
        message: 'Unable to sign out. Please try again later.'
      })
    },
    onCompleted: clearSignedInState
  })

  const basicAuthLogin = async (loginData: ILoginData): Promise<IBasicAuthLoginResult> => {
    const { data: response } = await loginFn({
      variables: { email: loginData.email, password: loginData.password }
    })
    const mfaRequired = await handleResponse(
      response?.signInUser?.token,
      response?.signInUser?.role,
      response?.signInUser?.user?.mfaEnabled,
      response?.signInUser?.webSessionGrant
    )

    if (!response) {
      return { mfaRequired: false }
    }

    return { response, mfaRequired }
  }

  const googleLogin = async (idToken: string): Promise<void> => {
    const { data: response } = await googleLoginFn({
      variables: { provider: ThirdPartySignInProvider.Google, providerToken: idToken }
    })
    await handleResponse(
      response?.signInThirdPartyUser?.token,
      response?.signInThirdPartyUser?.role,
      undefined,
      response?.signInThirdPartyUser?.webSessionGrant
    )
  }

  const finalizeAuthorization = async () => {
    await getAuthorizedFeatures()
    setLoggedIn(true)
  }

  // A web session replaces any long-lived token from an earlier sign-in here.
  // It starts without MFA, which the SMS challenge then records on it.
  const startWebSession = async (grant: string, mfaEnabled?: boolean): Promise<boolean> => {
    try {
      await exchangeWebSessionGrant(grant)
    } catch {
      throw LOGIN_FAILED
    }
    clearAuthToken()
    clearMfaToken()

    if (mfaEnabled) {
      return true
    }

    await finalizeAuthorization()
    return false
  }

  // Returns whether an SMS MFA challenge must be completed before the session is usable.
  const handleResponse = async (
    token?: string | null,
    role?: string | null,
    mfaEnabled?: boolean,
    webSessionGrant?: string | null
  ): Promise<boolean> => {
    if (webSessionGrant) {
      if (!role || role === UserRole.User) {
        throw NOT_AUTHORIZED
      }

      return startWebSession(webSessionGrant, mfaEnabled)
    }

    if (token !== null && role && role !== UserRole.User) {
      if (token) {
        // A web session from an earlier sign-in would otherwise take precedence.
        clearWebSession()
        setAuthToken(token)
      }

      // An internal admin signing in with email/password must pass the SMS challenge
      // before we fetch authorized features or mark them logged in — the backend rejects
      // every other operation with invalid_mfa_token until then. The auth token is set
      // above so the challenge mutations authenticate as this user.
      if (mfaEnabled && !getMfaToken()) {
        return true
      }

      const sentMfaToken = getMfaToken()
      try {
        await finalizeAuthorization()
      } catch (error) {
        if (
          !mfaEnabled ||
          !(error instanceof ApolloError) ||
          !isInvalidMfaTokenError(error.graphQLErrors)
        ) {
          throw error
        }

        // Another tab stored a new MFA token while this request was in flight:
        // authorize with it rather than discarding it. (On the login screen
        // errorLink has already cleared the rejected token, leaving none.)
        const storedMfaToken = getMfaToken()
        if (storedMfaToken && storedMfaToken !== sentMfaToken) {
          await finalizeAuthorization()
          return false
        }

        // The stored MFA token was expired or revoked: challenge for a new one.
        clearMfaToken()
        return true
      }
      return false
      // eslint-disable-next-line security/detect-possible-timing-attacks -- comparison against the null literal, not a secret
    } else if (token === null) {
      if (role !== UserRole.ExternalAdmin) {
        throw USE_GOOGLE_LOGIN
      } else {
        throw INVALID_CREDENTIALS
      }
    } else if (!role || role === UserRole.User) {
      throw NOT_AUTHORIZED
    } else {
      throw LOGIN_FAILED
    }
  }

  // Called by the MFA challenge screen once verifyMfaSmsCode returns a token: persist it
  // (subsequent requests attach it via mfaTokenLink) and finish authorizing the session.
  // A web session has recorded the challenge itself, so it keeps no token.
  const completeMfaChallenge = async (mfaToken: string) => {
    if (!hasWebSession()) {
      setMfaToken(mfaToken)
    }
    await finalizeAuthorization()
  }

  const isAuthorized = useCallback(
    (feature: FeatureGrant) => {
      if (!loggedIn && !currentUser) {
        return false
      }

      const authorizedFeatures = authorizedFeaturesData?.authorizedFeatures.features ?? []
      return authorizedFeatures.includes(feature)
    },
    [authorizedFeaturesData, loggedIn, currentUser]
  )

  const value = {
    isAuthorized,
    loading: loading || authorizing,
    basicAuthLogin,
    googleLogin,
    completeMfaChallenge,
    // A web session signs out at its own endpoint, so it doesn't need
    // signOutUser. The signed-in state is cleared without waiting for it.
    logout: async () => {
      if (hasWebSession()) {
        void endWebSession()
        clearSignedInState()
        return
      }

      await logoutFn()
    }
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
